import { useEffect, useLayoutEffect, useState, ReactNode, FC } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
} from '@chakra-ui/react';

export interface ChakraConfirmationDialogProps
  extends Omit<ModalProps, 'children' | 'onClose'> {
  title: string;
  actionButtonText?: string;
  cancelButtonText?: string;
  hideCancelButton?: boolean;
  handleAction: () => void;
  disableActions?: boolean;
  disableSubmitAction?: boolean;
  message?: string | ReactNode;
  handleClose?: (
    event?: {},
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => void;
  children?: ReactNode;
  onClose?: () => void;
  modalContentProps?: ModalContentProps;
}

export const ChakraConfirmationDialog: FC<ChakraConfirmationDialogProps> = ({
  title,
  actionButtonText = 'Ok',
  cancelButtonText = 'Cancel',
  children,
  handleAction,
  disableActions,
  disableSubmitAction,
  isOpen: isOpenProp,
  message: messageProp,
  onClose = () => {},
  handleClose: handleCloseProp,
  modalContentProps,
  hideCancelButton,
  ...rest
}) => {
  const [open, setOpen] = useState(isOpenProp);
  const [message, setMessage] = useState<string | ReactNode>();
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  useLayoutEffect(() => {
    setOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (isOpenProp) {
      setMessage(messageProp);
    }
  }, [isOpenProp, messageProp]);

  return (
    <Modal
      isOpen={open}
      onClose={handleCloseProp || handleClose}
      data-testid={`${title} dialog`}
      {...rest}
    >
      <ModalOverlay />
      <ModalContent {...modalContentProps}>
        <ModalHeader data-testid="dialog-title" title={title}>
          {title}
        </ModalHeader>
        <ModalBody data-testid="dialog-content">
          {message && (
            <Text
              fontSize="sm"
              lineHeight={1.58}
              data-testid="dialog-text"
              wordBreak="break-word"
            >
              {message}
            </Text>
          )}
          {children}
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          {!hideCancelButton && (
            <Button
              variant="secondary"
              onClick={handleCloseProp || handleClose}
              isDisabled={disableActions}
              data-testid="confirm-dialog-cancel-button"
            >
              {cancelButtonText}
            </Button>
          )}
          <Button
            variant="primary"
            ml={4}
            onClick={handleAction}
            isDisabled={disableActions || disableSubmitAction}
            data-testid="confirm-dialog-action-button"
          >
            {actionButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
